import { type ReactElement, type ReactNode } from 'react';
import { Box, BrandLockup } from '@citibox-panel/ui';

export interface PageHeaderProps {
  // The market this deployment writes against, resolved by the host: each deployment reaches only
  // its own app-service, so the label is what tells an engineer WHICH network they are about to
  // change. It belongs on the chrome, not only on a confirmation.
  brandLabel: string;
  brandDegraded: boolean;
  // The host's own chrome — the user menu, and with it the only way to sign out of this screen.
  headerEnd?: ReactNode;
}

// The chrome band of the operations surfaces, deliberately wearing the same lockup, height and rule
// as the search shell's: an engineer moving between surfaces must not feel they left the panel.
// Shared by this module's slices, still duplicated against the search shell's band (ADR 0019); only
// the lockup inside it is a shared façade. Keeping the numbers below in step with that one is a
// review obligation, and the tests are what makes a drift visible.
//
// What this surface does NOT carry is the search field, the «nueva búsqueda» affordance and the tab
// strip: there is no entity here and nothing to search for, so they are absent rather than present
// and inert.
export function PageHeader({ brandLabel, brandDegraded, headerEnd }: PageHeaderProps): ReactElement {
  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        // Pinned, not a minimum — the same reasoning as the other surface: a band that changes
        // height is read as the page moving.
        height: 68,
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        // White chrome over the surface gray the content sits on.
        bgcolor: 'background.paper',
        zIndex: 1100,
      }}
    >
      <BrandLockup label={brandLabel} degraded={brandDegraded} />
      <Box sx={{ flexGrow: 1 }} />
      {headerEnd}
    </Box>
  );
}
